import {
  PAYMENT_DEFAULT_VALUE_USED_CAR,
  PAYMENT_METHOD,
} from '~/data/searchFlowData';
import { EUrlParam } from '~/data/urlParams';
import { useMemo } from 'react';
import { useAppSelector } from '~/redux-toolkit/store/hooks';
import { RootState } from '~/redux-toolkit/store/store';
import { useCurrentUrlParams } from '~/hooks/useCurrentUrlParams';

export const useUsedCarBuildURLParams = (isNotFromRP?: boolean) => {
  const currentUrlParams = useCurrentUrlParams();
  const { usedCarQuery } = useAppSelector(
    (state: RootState) => state.usedCarFilters,
  );
  const { filters } = usedCarQuery;

  const budgetFilter = useMemo(() => filters.budget, [filters]);
  const kmFilter = useMemo(() => filters.kmRange, [filters]);

  const minPrice = useMemo(
    () =>
      budgetFilter.priceRange.minPrice
        ? budgetFilter.priceRange.minPrice
        : budgetFilter.paymentMethod === PAYMENT_METHOD.monthly
        ? PAYMENT_DEFAULT_VALUE_USED_CAR.monthly.minPrice
        : PAYMENT_DEFAULT_VALUE_USED_CAR.oneTime.minPrice,
    [budgetFilter],
  );
  const maxPrice = useMemo(
    () =>
      budgetFilter.priceRange.maxPrice
        ? budgetFilter.priceRange.maxPrice
        : budgetFilter.paymentMethod === PAYMENT_METHOD.monthly
        ? PAYMENT_DEFAULT_VALUE_USED_CAR.monthly.maxPrice
        : PAYMENT_DEFAULT_VALUE_USED_CAR.oneTime.maxPrice,
    [budgetFilter],
  );

  if (
    budgetFilter.priceRange.minPrice ||
    budgetFilter.priceRange.minPrice === 0 ||
    isNotFromRP
  ) {
    currentUrlParams.set(EUrlParam.PAYMENT_METHOD, budgetFilter.paymentMethod);
    currentUrlParams.set(EUrlParam.MIN_PRICE, minPrice.toString());
    currentUrlParams.set(EUrlParam.MAX_PRICE, maxPrice.toString());
  }

  if (
    !budgetFilter.priceRange.minPrice &&
    budgetFilter.priceRange.minPrice !== 0
  ) {
    currentUrlParams.delete(EUrlParam.PAYMENT_METHOD);
    currentUrlParams.delete(EUrlParam.MIN_PRICE);
    currentUrlParams.delete(EUrlParam.MAX_PRICE);
  }

  if (kmFilter.minPrice && kmFilter.maxPrice) {
    currentUrlParams.set(EUrlParam.MIN_KM, kmFilter.minPrice.toString());
    currentUrlParams.set(EUrlParam.MAX_KM, kmFilter.maxPrice.toString());
  }
  if (!kmFilter.minPrice && !kmFilter.maxPrice) {
    currentUrlParams.delete(EUrlParam.MIN_KM);
    currentUrlParams.delete(EUrlParam.MAX_KM);
  }

  if (filters.colors.length) {
    currentUrlParams.set(EUrlParam.COLORS, filters.colors.join(','));
  } else {
    currentUrlParams.delete(EUrlParam.COLORS);
  }

  if (filters.categories.length) {
    currentUrlParams.set(EUrlParam.CATEGORIES, filters.categories.join(','));
  } else {
    currentUrlParams.delete(EUrlParam.CATEGORIES);
  }

  if (filters.models.length) {
    currentUrlParams.set(EUrlParam.MODELS, filters.models.join(','));
  } else {
    currentUrlParams.delete(EUrlParam.MODELS);
  }

  if (filters.brands.length) {
    currentUrlParams.set(EUrlParam.BRANDS, filters.brands.join(','));
  } else {
    currentUrlParams.delete(EUrlParam.BRANDS);
  }
  if (filters.carHighlights.length) {
    currentUrlParams.set(EUrlParam.HIGHLIGHTS, filters.carHighlights.join(','));
  } else {
    currentUrlParams.delete(EUrlParam.HIGHLIGHTS);
  }
  if (filters.carBodyType.length) {
    currentUrlParams.set(EUrlParam.BODY_TYPES, filters.carBodyType.join(','));
  } else {
    currentUrlParams.delete(EUrlParam.BODY_TYPES);
  }

  if (filters.gearType.length) {
    currentUrlParams.set(EUrlParam.GEAR, filters.gearType.join(','));
  } else {
    currentUrlParams.delete(EUrlParam.GEAR);
  }

  if (filters.engineType.length) {
    currentUrlParams.set(EUrlParam.FUEL_TYPE, filters.engineType.join(','));
  } else {
    currentUrlParams.delete(EUrlParam.FUEL_TYPE);
  }
  if (filters.carHand.length) {
    currentUrlParams.set(EUrlParam.CAR_HAND, filters.carHand.join(','));
  } else {
    currentUrlParams.delete(EUrlParam.CAR_HAND);
  }
  if (filters.imagesLength) {
    currentUrlParams.set(
      EUrlParam.ONLY_WITH_IMAGES,
      filters.imagesLength.toString(),
    );
  } else {
    currentUrlParams.delete(EUrlParam.ONLY_WITH_IMAGES);
  }
  if (filters.originOwnership.length) {
    currentUrlParams.set(
      EUrlParam.ORIGIN_OWNERSHIP,
      filters.originOwnership.join(','),
    );
  } else {
    currentUrlParams.delete(EUrlParam.ORIGIN_OWNERSHIP);
  }
  if (
    filters.yearsRange.minYear?.length &&
    filters.yearsRange.maxYear?.length
  ) {
    currentUrlParams.set(EUrlParam.MIN_YEAR, filters.yearsRange.minYear);
    currentUrlParams.set(EUrlParam.MAX_YEAR, filters.yearsRange.maxYear);
  }
  if (
    !filters.yearsRange.minYear?.length &&
    !filters.yearsRange.maxYear?.length
  ) {
    currentUrlParams.delete(EUrlParam.MIN_YEAR);
    currentUrlParams.delete(EUrlParam.MAX_YEAR);
  }

  return currentUrlParams.toString();
};
